// Imports
import { UserStatus } from '@vue-util/cookie';
import { sanitize } from '@vue-util/sanitize';
import logger from '@vue-util/logger';
// Utility functions and constants
import { buildKey, prepareReturnItemsProperty, fireApiErrorTag } from '@/utils/returns/General';
import { getLoyaltyStatus } from '@/utils/UserDetails';
import { errorHandler } from '@/utils/ErrorHandler';
import { CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS, NOTIFICATION_TYPES } from '#shared/constants';
import { UOP_SUBMIT_RETURN_ENDPOINT, RP_SUBMIT_RETURN_ENDPOINT_V2 } from '#shared/endpoints';

// Config
import * as config from 'config';

// Pinia Store & Composables
import { useJoinStarRewardsStore } from '@/stores/useJoinStarRewardsStore';
import { useReturnConfirmationStore } from '@/stores/returns/useReturnConfirmationStore';
import { useEstimatedRefundStore } from '@/stores/returns/useEstimatedRefundStore';
import { useFullScreenLoader } from '@/composables/useFullScreenLoader';
import useBrand from '@/composables/useBrand';

// Services
import { returnsService } from '@/services/returns/returnItemsService';

const { isBcom, isMcom } = useBrand();

export const useReturnItemStore = defineStore('returnItemStore', {
  state: () => ({
    returnItems: null,
    loading: {
      getReturnItems: true,
      submitReturnItems: false,
      getShippingDetails: false,
      prepareDeclarationResponse: false,
    },
    errors: {
      getReturnItems: false,
      submitReturnItems: null,
      getShippingDetails: false,
      prepareDeclarationResponse: false,
      v2SubmitReturnError: null,
    },
    returnSelections: {
      returnItems: [],
      returnItemsDraft: [],
      refundOption: null,
      returnMethod: '',
      shippingAddressDetails: null,
    },
    returnItemsCurrentStep: 1, // Return Items Step
    returnItemsEditable: {
      returnItems: true,
      refundOptions: false,
      returnMethod: false,
      shippingAddress: false,
    },
    shippingDetails: {},
    prepareDeclarationResponse: {
      resolutionOptions: null,
      cartonLines: [],
    },
    isReturnMethodIneligible: false,
    hasEnrolledForStarRewards: false,
    hasAddedItemsFromDifferentSeller: false,
    isInvalidUpc: false,
    hasEmptyCartonLine: false,
    exchangeItem: {
      draft: {},
      selected: {},
    },
  }),
  getters: {
    IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE(state) {
      const isSignedIn = UserStatus.isSignedIn();
      if (config.brand === 'bcom') return false;
      const isVirtualReturnItemSelected = state.returnSelections.returnItems.some((item) => item.returnType !== 'PR');
      const hasLoyaltyStatus = getLoyaltyStatus();
      const isLoyaltyMember =
        (isSignedIn && hasLoyaltyStatus) ||
        (isSignedIn && !hasLoyaltyStatus && useJoinStarRewardsStore().starRewardsSummary.data?.encryptedLoyaltyId);
      return (
        (state.returnItems?.conditionalReturnFeeEnabled &&
          this.IS_PREFERRED_RETURN_REASON_SELECTED &&
          !this.IS_REGISTRY_ORDER &&
          !this.IS_MARKETPLACE_ORDER &&
          !isVirtualReturnItemSelected &&
          !isLoyaltyMember) ??
        false
      );
    },
    GET_SELECTED_RETURN_ITEMS(state) {
      return state.returnItems?.cartonLines?.filter((item) => {
        const key = buildKey(item);
        return state.returnSelections.returnItemsDraft.some((returnItem) => returnItem.key === key);
      });
    },
    IS_MARKETPLACE_ORDER(state) {
      return state.returnItems?.isMkpOrder ?? false;
    },
    IS_ACCOUNT_VERIFIED(state) {
      return !!state.returnItems?.beneficiaryUserDetails?.isAccountVerified;
    },
    IS_REGISTRY_ORDER() {
      return this.GET_SELECTED_RETURN_ITEMS?.some((item) => item.registryBarcode && item.registryBarcode !== 0);
    },
    IS_PREFERRED_RETURN_REASON_SELECTED(state) {
      return state.returnSelections.returnItems.every((item) => {
        const returnReason = state.returnItems?.returnReason.find((reason) => reason.code === item.returnReason);
        return returnReason?.shippingChargeIndicator === 'Y';
      });
    },
    IS_FEDEX_ELIGIBLE(state) {
      if (!isBcom) return false;
      const cartonLineMap = new Map(state.returnItems?.cartonLines.map((item) => [buildKey(item), item]));
      return state.returnSelections.returnItems.every((selectedItem) => {
        const cartonLine = cartonLineMap.get(selectedItem.key);
        return cartonLine?.itemDetails.isFedExReturnEligible;
      });
    },
    IS_REPLACE_ELIGIBLE(state) {
      if (isMcom) return false;
      const { returnItems, prepareDeclarationResponse } = state;
      return (
        returnItems?.isReplacementEnabled &&
        returnItems.shippingAddresses?.length > 0 &&
        prepareDeclarationResponse.isReplaceOrExchangeApplicable &&
        state.prepareDeclarationResponse.cartonLines.some((item) => item.resolutionOptions.includes('REPLACEMENT'))
      );
    },
    IS_EXCHANGE_ELIGIBLE(state) {
      if (isMcom) return false;
      const { returnItems, prepareDeclarationResponse } = state;
      return (
        returnItems?.isExchangeEnabled &&
        returnItems.shippingAddresses?.length > 0 &&
        prepareDeclarationResponse.isReplaceOrExchangeApplicable &&
        prepareDeclarationResponse.cartonLines.some((item) => item.resolutionOptions.includes('EXCHANGE'))
      );
    },
    SUBMIT_DISABLED(state) {
      return Object.values(state.returnItemsEditable).some((key) => key);
    },
    SHOW_CONDITIONAL_RETURN_FEE(state) {
      return (
        isMcom &&
        state.returnItems?.conditionalReturnFeeEnabled &&
        CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS.includes(state.returnSelections.returnMethod) &&
        !this.IS_MARKETPLACE_ORDER
      );
    },
    APPLY_CONDITIONAL_RETURN_FEE(state) {
      return (
        this.IS_ELIGIBLE_FOR_CONDITIONAL_RETURN_FEE &&
        !state.hasEnrolledForStarRewards &&
        CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS.includes(state.returnSelections.returnMethod)
      );
    },
    GET_RETURN_NOTIFICATION(state) {
      const showAlertMessage =
        state.returnItems?.orderReturnAlertMessage.web?.message_type &&
        state.returnItems?.orderReturnAlertMessage.web?.message &&
        NOTIFICATION_TYPES.includes(state.returnItems?.orderReturnAlertMessage.web?.message_type?.toLowerCase());

      const notifications = [];
      if (state.errors.getReturnItems) {
        notifications.push({
          text: config.RETURN_ITEMS_ERROR,
          severity: 'error',
        });
      }
      if (showAlertMessage) {
        notifications.push({
          text: sanitize(state.returnItems?.orderReturnAlertMessage.web?.message),
          severity: state.returnItems?.orderReturnAlertMessage.web?.message_type?.toLowerCase(),
        });
      }
      if (state.isInvalidUpc) {
        notifications.push({
          text: config.RETURN_INVALID_UPC,
          severity: 'error',
        });
      }
      if (state.hasEmptyCartonLine) {
        notifications.push({
          text: config.RETURN_EMPTY_CARTON_LINE,
          severity: 'error',
        });
      }
      if (state.errors.prepareDeclarationResponse) {
        notifications.push({
          text: config.PREPARE_DECLARATION_ERROR,
          severity: 'error',
        });
      }
      if (state.errors.v2SubmitReturnError) {
        notifications.push({
          text: state.errors.v2SubmitReturnError,
          severity: 'error',
          closable: true,
        });
      }
      return notifications;
    },
    GET_ESTIMATED_REFUND_SUBMIT_ERROR(state) {
      if (state.errors.submitReturnItems) {
        return state.errors.submitReturnItems;
      }
      const estimatedRefundStore = useEstimatedRefundStore();
      const error = estimatedRefundStore.estimatedRefundError;
      if (error) {
        return error;
      }
      return null;
    },
    CONTAINS_AREA_RUG_ITEM(state) {
      const cartonLineMap = new Map(state.returnItems?.cartonLines.map((item) => [buildKey(item), item]));
      return state.returnSelections.returnItems.some((selectedItem) => {
        const cartonLines = cartonLineMap.get(selectedItem.key);
        return cartonLines?.itemDetails.department.departmentId === 911;
      });
    },
    SHOW_SHIPPING_ADDRESS_SECTION(state) {
      const { refundOption } = state.returnSelections;
      const isEligibleResolutionOption = [config.REFUND_METHOD.EXCHANGE, config.REFUND_METHOD.REPLACEMENT].includes(refundOption?.choice);
      return isEligibleResolutionOption && (this.IS_EXCHANGE_ELIGIBLE || this.IS_REPLACE_ELIGIBLE);
    },
  },
  actions: {
    // TODO: In future it will depends on the API response, for now we are hardcoding it to true
    SET_ACCOUNT_VERIFIED(isVerified) {
      if (this.returnItems && this.returnItems.beneficiaryUserDetails) {
        this.returnItems.beneficiaryUserDetails.isAccountVerified = isVerified;
      }
    },
    SET_EXCHANGE_ITEM(payload = { draft: null, reset: false }) {
      if (payload.reset) {
        this.exchangeItem = {
          draft: {},
          selected: {},
        };
        return;
      }
      if (!payload.draft) {
        this.exchangeItem.selected = { ...this.exchangeItem.draft };
      } else {
        this.exchangeItem.draft = { ...payload.draft };
      }
    },
    SET_LOADER(payload) {
      this.loading = { ...this.loading, ...payload };
    },
    SET_ERRORS(payload) {
      this.errors = { ...this.errors, ...payload };
    },
    SET_RETURN_ITEMS(returnItems) {
      this.returnItems = returnItems;
    },
    SET_SHIPPING_DETAILS(shippingDetails) {
      this.shippingDetails = shippingDetails;
    },
    SET_RETURN_SELECTIONS(payload = {}) {
      this.returnSelections = {
        ...this.returnSelections,
        ...payload,
      };
    },
    SET_RETURN_ITEMS_CURRENT_STEP(step) {
      this.returnItemsCurrentStep = step;
    },
    SET_RETURN_EDITABLE(payload) {
      this.returnItemsEditable = payload;
    },
    SET_RETURN_METHOD_INELIGIBLE(flag) {
      this.isReturnMethodIneligible = flag;
    },
    SET_HAS_ENROLLED_FOR_STARREWARDS(value) {
      this.hasEnrolledForStarRewards = value;
    },
    SET_HAS_ITEMS_FROM_DIFFERENT_SELLER(value) {
      this.hasAddedItemsFromDifferentSeller = value;
    },
    SET_IS_INVALID_UPC(value) {
      this.isInvalidUpc = value;
    },
    SET_EMPTY_CARTON_LINE(value) {
      this.hasEmptyCartonLine = value;
    },
    SET_PREPARE_DECLARATION_RESPONSE(payload) {
      this.prepareDeclarationResponse = payload;
    },

    /**
     * APIs
     */
    async GET_SHIPPING_DETAILS({ pidToken }) {
      this.SET_LOADER({ getShippingDetails: pidToken });
      this.SET_ERRORS({ getShippingDetails: false });
      try {
        const response = await returnsService.fetchShippingDetails({
          pidToken,
        });
        this.SET_SHIPPING_DETAILS(response);
      } catch (error) {
        // NOSONAR
        const { errorCode = '', message = '' } = error?.response?.data?.errors?.error?.[0] ?? { errorCode: '', message: '' };
        this.SET_ERRORS({ getShippingDetails: pidToken });
        fireApiErrorTag({
          errorLabel: 'Get return label by pidToken',
          message: config.RMA_SHIPPING_DETAILS_ERROR,
          errorCode,
          miscMessage: message,
        });
        setTimeout(() => {
          this.SET_ERRORS({ getShippingDetails: false });
        }, 5000);
      } finally {
        this.SET_LOADER({ getShippingDetails: false });
      }
    },
    async GET_SHIPPING_DETAILS_BY_RMA_ID({ rmaId }) {
      this.SET_LOADER({ getShippingDetails: rmaId });
      this.SET_ERRORS({ getShippingDetails: false });
      try {
        const response = await returnsService.fetchShippingDetailsByRmaId({
          rmaId,
        });
        this.SET_SHIPPING_DETAILS(response);
      } catch {
        // NOSONAR
        this.SET_ERRORS({ getShippingDetails: rmaId });
        setTimeout(() => {
          this.SET_ERRORS({ getShippingDetails: false });
        }, 5000);
      } finally {
        this.SET_LOADER({ getShippingDetails: false });
      }
    },
    async GET_RETURN_ITEMS({ token, upc, ssrContext = {} }) {
      this.SET_LOADER({ getReturnItems: true });
      this.SET_ERRORS({ getReturnItems: false });
      try {
        const response = await returnsService.fetchReturnItems({ token, ssrContext });
        this.SET_RETURN_ITEMS(response.data.returnItems);
        const selectedReturnItems = [];
        if (upc) {
          const selectedItem = this.returnItems?.cartonLines.filter((item) => item.upc === upc);
          if (!selectedItem?.length || selectedItem.every((item) => !item.itemDetails.returnEligibleFlag)) {
            this.SET_IS_INVALID_UPC(true);
            logger.info(`[PPE] GET_RETURN_ITEMS: INVALID UPC - upc not available in cartonLines`);
            return null;
          }
          selectedItem.forEach((item) => {
            selectedReturnItems.push(prepareReturnItemsProperty(item));
          });
        } else if (this.returnItems.cartonLines?.length === 1) {
          const selectedItem = this.returnItems.cartonLines[0];
          if (!selectedItem.itemDetails?.returnEligibleFlag) {
            this.SET_IS_INVALID_UPC(true);
            logger.info(`[PPE] GET_RETURN_ITEMS: INVALID UPC - returnEligibleFlag = false for selectedItem`);
            return null;
          }
          selectedReturnItems.push(prepareReturnItemsProperty(selectedItem));
        } else if (!this.returnItems.cartonLines?.length) {
          this.SET_EMPTY_CARTON_LINE(true);
          logger.info(`[PPE] GET_RETURN_ITEMS: CARTONLINE EMPTY`);
          return null;
        }
        this.SET_RETURN_SELECTIONS({ returnItemsDraft: selectedReturnItems });
        logger.info(`[PPE] GET_RETURN_ITEMS: SUCCESS`);
        return null;
      } catch (error) {
        const { errorCode = '', message = '' } = error?.response?.data?.errors?.error?.[0] ?? {};
        this.SET_ERRORS({ getReturnItems: true });
        logger.info(
          `[PPE] GET_RETURN_ITEMS: ERROR Caught: ${JSON.stringify({
            status: error?.status,
            data: error?.response?.data,
            message: error?.message,
          })}`,
        );
        return {
          errorCode,
          message: config.RETURN_ITEMS_ERROR,
          miscMessage: message,
          errorLabel: 'get return items',
        };
      } finally {
        this.SET_LOADER({ getReturnItems: false });
      }
    },
    async SUBMIT_PREPARE_DECLARATION() {
      this.SET_LOADER({ prepareDeclarationResponse: true });
      this.SET_ERRORS({ prepareDeclarationResponse: false });

      try {
        const isSignedIn = UserStatus.isSignedIn();
        const payload = returnsService.createPrepareDeclarationPayload({
          returnItems: this.returnItems,
          returnSelections: this.returnSelections,
          isSignedIn,
        });
        const response = await returnsService.submitPrepareDeclaration({
          payload,
          verifyLookup: !isSignedIn ? 'true' : 'false',
        });
        this.SET_PREPARE_DECLARATION_RESPONSE(response.data.prepareDeclarationResponse);
      } catch (error) {
        const { errorCode = '', message = '' } = error?.response?.data?.errors?.error?.[0] ?? {};
        fireApiErrorTag({
          errorCode,
          message: config.PREPARE_DECLARATION_ERROR,
          miscMessage: message,
          errorLabel: 'prepare declaration',
        });
        this.SET_PREPARE_DECLARATION_RESPONSE({});
        this.SET_ERRORS({ prepareDeclarationResponse: true });
      } finally {
        this.SET_LOADER({ prepareDeclarationResponse: false });
      }
    },
    async SUBMIT_RETURN_ITEMS({ router, upcNumber }) {
      const { showFullScreenLoader, hideFullScreenLoader } = useFullScreenLoader();
      const isSignedIn = UserStatus.isSignedIn();
      const isExchangeOrReplaceSelected = [config.REFUND_METHOD.EXCHANGE, config.REFUND_METHOD.REPLACEMENT].includes(
        this.returnSelections.refundOption?.choice,
      );
      if (isExchangeOrReplaceSelected) showFullScreenLoader(config.FULLSCREEN_LOADER_MESSAGE);
      this.SET_LOADER({ submitReturnItems: true });
      this.SET_ERRORS({ submitReturnItems: null });
      this.SET_ERRORS({ v2SubmitReturnError: null });

      const estimatedRefundStore = useEstimatedRefundStore();
      const useRpApi = returnsService.shouldUseRpApi({
        returnItems: this.returnItems,
        returnSelections: this.returnSelections,
        estimatedRefund: estimatedRefundStore.estimatedRefund,
        declarationId: this.prepareDeclarationResponse.declarationId,
        returnMethods: this.prepareDeclarationResponse.returnMethods,
      });
      const payload = returnsService.createSubmitReturnPayload({
        returnItems: this.returnItems,
        returnSelections: this.returnSelections,
        prepareDeclarationResponse: this.prepareDeclarationResponse,
        exchangeItem: this.exchangeItem,
        isSignedIn,
        useRpApi,
        applyConditionalReturnFee: this.APPLY_CONDITIONAL_RETURN_FEE,
        isExchangeOrReplaceSelected,
      });
      const SUBMIT_RETURN_ENDPOINT = useRpApi ?? UOP_SUBMIT_RETURN_ENDPOINT;
      try {
        const response = await returnsService.submitReturn({
          payload,
          isSignedIn,
          endpoint: SUBMIT_RETURN_ENDPOINT,
        });
        const returnConfirmationStore = useReturnConfirmationStore();
        returnConfirmationStore.SET_RETURN_CONFIRMATION(response.data);
        await router?.replace({ name: 'Return Confirmation' }); // TODO use router directly inside store instead of passing from component
      } catch (error) {
        const { errorCode = null, message = null } = error?.response?.data?.errors?.error?.[0] ?? { errorCode: null, message: null };
        const isV2SubmitApi = useRpApi === RP_SUBMIT_RETURN_ENDPOINT_V2;
        if (isV2SubmitApi && errorCode && message && ['17000', '17001'].includes(errorCode)) {
          this.SET_ERRORS({ v2SubmitReturnError: errorHandler(message, config.RETURN_SUBMIT_ERROR) });
          this.RESET_TO_STEP_ONE({ upcNumber });
          fireApiErrorTag({
            errorCode,
            message,
            miscMessage: message,
            errorLabel: 'v2 submit return items',
          });
        } else {
          this.SET_ERRORS({ submitReturnItems: errorHandler(null, config.RETURN_SUBMIT_ERROR) });
          fireApiErrorTag({
            errorCode,
            message: config.RETURN_SUBMIT_ERROR,
            miscMessage: message,
            errorLabel: 'submit return items',
          });
        }
        this.SET_LOADER({ submitReturnItems: false });
      } finally {
        hideFullScreenLoader();
      }
    },

    CLEAR_RETURN_ITEMS_DETAILS() {
      this.SET_RETURN_ITEMS(null);
      this.SET_RETURN_ITEMS_CURRENT_STEP(1);
      this.SET_RETURN_EDITABLE({
        returnItems: true,
        refundOptions: false,
        returnMethod: false,
      });
      this.SET_RETURN_SELECTIONS({
        returnItems: [],
        returnItemsDraft: [],
        refundOption: null,
        returnMethod: '',
      });
      this.SET_EXCHANGE_ITEM({ reset: true });
      this.SET_RETURN_METHOD_INELIGIBLE(false);
      this.SET_HAS_ENROLLED_FOR_STARREWARDS(false);
      this.SET_HAS_ITEMS_FROM_DIFFERENT_SELLER(false);
      this.SET_IS_INVALID_UPC(false);
    },

    /**
     * Resets the return flow to Step 1.
     * This is specifically used when an MKP V2 error occurs, requiring the page to be reset as if the user just landed,
     * while still displaying the error message.
     * @param {Object} params - The parameters object.
     * @param {string} params.upcNumber - The UPC number to preserve as selected.
     */
    RESET_TO_STEP_ONE({ upcNumber }) {
      let selectedItem = this.returnItems?.cartonLines?.[0];
      if (this.returnItems?.cartonLines?.length > 0 && upcNumber != null) {
        selectedItem = this.returnItems.cartonLines.find((item) => item.upc.toString() === upcNumber.toString());
      }
      this.SET_RETURN_SELECTIONS({
        returnItemsDraft: selectedItem ? [prepareReturnItemsProperty(selectedItem)] : [],
        returnItems: [],
        refundOption: null,
        returnMethod: '',
        shippingAddressDetails: null,
      });
      this.SET_RETURN_EDITABLE({
        returnItems: true,
        refundOptions: false,
        returnMethod: false,
      });
      this.SET_EXCHANGE_ITEM({ reset: true });
      this.SET_RETURN_ITEMS_CURRENT_STEP(1);
    },
  },
});
